import { CITY } from '@city'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Outlet, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useSyncExternalStore } from 'react'
import { MetroMapStoreProvider } from '../state/MetroMapStoreProvider'
import { useMetroMapStore, useMetroMapDispatch } from '../state/useMetroMapStore'
import { RouteSelectionLocalStorageSync } from '../state/RouteSelectionLocalStorageSync'
import { RouteSelectionUrlSync } from '../state/RouteSelectionUrlSync'
import { RoutingController } from '../state/RoutingController'
import { RoutingVariantSync } from '../state/RoutingVariantSync'
import { buildSlugIndex, type SlugIndex } from '../state/slugIndex'
import { buildCarouselCluster } from '../state/carouselCluster'
import { effectiveTheme } from '../state/effectiveTheme'
import { resolveStation, lineSlugFromId, resolveStationById } from '../interaction/stationResolver'
import { stationSnapshotFromSchema } from '../state/stationSnapshotFromSchema'
import { MetroMapCanvas } from './MetroMapCanvas'
import { SchemaProvider } from './SchemaProvider'
import { useSchema } from './useSchema'
import { StationCardOrDetailsRouter, BREAKPOINT_PX } from './StationCardOrDetailsRouter'
import { LineUrlSync } from '../lineView/LineUrlSync'
import { LineViewHost } from '../lineView/LineViewHost'
import { StationDetailsModal } from './StationDetailsModal'
import { noopHiddenRowHandler } from './AppShellHandlers'
import { realMetroAnalyticsReporter } from '../analytics/webAnalyticsReporter'
import { MobileRouteCardOverlay } from './MobileRouteCardOverlay'
import { ModesToggle } from './ModesToggle'
import { MapAppCta } from './MapAppCta'
import { AppPromptHost } from '../appPrompt/AppPromptHost'
import { buildStationDetailsProps } from './buildStationDetailsProps'
import { useStationCardAnalytics } from './useStationCardAnalytics'
import { useLineStatusViewedAnalytics } from './useLineStatusViewedAnalytics'
import { resolveStatusVariant } from '@metro/station-card'
import { reportHealthError, HEALTH_ID } from '../analytics/appHealthEvents'
import { favoritesService } from '../state/suggesterServices'
import { runLegacyIdMigration } from '../state/legacyIdMigration'
import { GeolocationController } from './GeolocationController'
import { MyLocationFloatingButton } from './MyLocationFloatingButton'
import { StationStatusController } from './StationStatusController'
import { useStationStatuses } from '../state/stationStatusContext'
import { FeedbackControllerProvider } from '../state/feedbackController'
import { useFeedback } from '../state/useFeedback'
import { NightThemeController } from '../state/nightThemeController'
import { NightAutoSwitchController } from '../state/NightAutoSwitchController'
import { NightTransitionLoader } from './NightTransitionLoader'

/**
 * Top-level layout: the map is mounted here and stays mounted for the
 * entire session. Route content (StationDetailHost, future Line / Oyster /
 * FAQ routes) renders in <Outlet /> over the map — app-shell pattern
 * per `[[station-deep-link-routes]]`. Navigation between routes never
 * remounts MetroMapCanvas: React Router only swaps the outlet, the
 * layout component (this one) keeps its identity.
 *
 * Provider stack: `SchemaProvider` → `MetroMapStoreProvider`.
 * `SchemaProvider` sits at the top (it has no upstream dependency); every
 * consumer beneath `MetroMapStoreProvider` reads the parsed schema and the
 * MetroMap store.
 *
 * Route-builder hydration order (URL > localStorage > empty): inside
 * `<MetroMapStoreProvider>` we mount `<RouteSelectionUrlSync>` BEFORE
 * `<RouteSelectionLocalStorageSync>` so the URL's `?from=&to=` query
 * populates the `routeSelection` slice first; the localStorage sync
 * then runs and bails when it sees a non-empty store. This makes the
 * URL the single source of truth for shared/deep-linked routes while
 * keeping localStorage as a fallback when the user reloads without
 * route params.
 */
export function AppShell() {
  return (
    <SchemaProvider>
      <MetroMapStoreProvider>
        <StationStatusController>
          <RoutingController>
            <GeolocationController>
              <FeedbackControllerProvider>
                {CITY.nightService && <NightThemeController />}
                {CITY.nightService && <NightTransitionLoader />}
                {CITY.nightWindow?.autoSwitch === true && (
                  <NightAutoSwitchController
                    nightWindow={CITY.nightWindow}
                    timeZone={CITY.timeZone}
                  />
                )}
                <MetroMapCanvas />
                <MyLocationFloatingButton />
                {CITY.nightService && <ModesToggle />}
                {CITY.storeApps && <MapAppCta />}
                {CITY.storeApps && <AppPromptHost />}
                <Outlet />
                <UrlToStoreSync />
                <RouteSelectionUrlSync />
                <RouteSelectionLocalStorageSync />
                <RoutingVariantSync />
                <StationDetailHost />
                <SelectedStationUrlSync />
                <LineUrlSync />
              </FeedbackControllerProvider>
            </GeolocationController>
          </RoutingController>
        </StationStatusController>
      </MetroMapStoreProvider>
    </SchemaProvider>
  )
}

/**
 * Exposes an id↔slug lookup used by SelectedStationUrlSync to keep the
 * URL in sync with the reducer's `selectedStation.id`. Reads the parsed
 * schema from `<SchemaProvider>` (single owner of `fetchSchema()` —
 * mounted at AppShell level), so no per-component fetch state and no
 * race with route remounts.
 */
function useSlugIndex(): SlugIndex | null {
  const schema = useSchema()
  return useMemo(() => (schema === null ? null : buildSlugIndex(schema)), [schema])
}

/**
 * Watches the store's `selectedStation` and reflects it into the URL
 * (`/stations/<slug>`). Keeps tap → URL in sync; the reverse
 * direction (URL → selectedStation) lives in <UrlToStoreSync>.
 */
function SelectedStationUrlSync() {
  const store = useMetroMapStore()
  const navigate = useNavigate()
  const slugIndex = useSlugIndex()
  const schema = useSchema()
  // One-shot rewrite of ids a pre-v2 client stored (recents, favourites
  // still on v1 record ids) once the map is loaded — TASK-986. Idempotent
  // behind its own flag, so a re-render or a revisit is safe.
  useEffect(() => {
    if (schema === null) return
    void runLegacyIdMigration(schema, favoritesService)
  }, [schema])
  const { locale: urlLocale } = useParams<{ locale?: string }>()
  // Every URL the SPA emits must carry the active locale prefix; without
  // it, RootLocaleRedirect (mounted on the locale-less SEO paths) would
  // intercept and re-route to the user's Accept-Language guess, fighting
  // this writer in an infinite loop. Fall back to the captured locale
  // path segment so the prefix is preserved across rewrites.
  const localePath = urlLocale !== undefined ? `/${urlLocale}` : ''
  const selectedStation = useSyncExternalStore(
    store.subscribe,
    () => store.getState().selectedStation,
  )
  // Per-event hint: map taps and direct URL landings push history
  // (urlReplaceMode=false), carousel-settle replaces the current entry
  // (urlReplaceMode=true) so back-button skips swiped-through cards.
  const urlReplaceMode = useSyncExternalStore(
    store.subscribe,
    () => store.getState().urlReplaceMode,
  )

  // Track whether we've ever seen a non-null selection. On cold start at
  // /stations/<slug>, the store starts null and the URL→Store sync runs
  // asynchronously (waits for schema). Without this guard, the null →
  // navigate-home branch fires first and pushes '/' onto history before
  // UrlToStoreSync has a chance to seed the selection — corrupting the
  // back-button (TASK-253 user-reported bug). Once we've seen a
  // selection, going null is a deliberate user action (cleared) and we
  // do navigate home.
  const hasEverSelectedRef = useRef(false)
  // Flip the latch as a side-effect of a non-null `selectedStation`,
  // not at render time. The lint rule `react-hooks/refs` flags ref
  // mutation during render; the latch's contract is unchanged because
  // both the setter and the consumer (the next useEffect) live in the
  // commit phase, so the consumer always sees the post-commit value.
  useEffect(() => {
    if (selectedStation !== null) hasEverSelectedRef.current = true
  }, [selectedStation])

  useEffect(() => {
    if (slugIndex === null) return
    // Stale-render guard: on a history pop the URL → store sync (which runs
    // first) may already have cleared or changed the selection this render
    // still holds — writing the old station back would push it over the
    // entry the user just popped to (Back from a station opened out of the
    // line view re-pushed that station). The store's own re-render brings
    // the effect back with the current value.
    if (store.getState().selectedStation !== selectedStation) return
    // Mid-transition guard (TASK-699 back-button trap): on a history pop
    // to a surface OUTSIDE the map — the station-page template
    // `/station/<id>`, legal pages — this effect runs once more BEFORE
    // AppShell unmounts, with the selection still in the store, and would
    // navigate straight back to `/stations/<slug>`, overwriting the entry
    // the user just popped to (station page → map → Back re-landed on the
    // map). This writer only owns URLs on the map's own surfaces: the
    // locale index, `/stations/*`, `/route` and `/lines/*`.
    {
      const currentPath = window.location.pathname
      const onMapSurface =
        currentPath === '/' ||
        currentPath === localePath ||
        currentPath === `${localePath}/` ||
        currentPath.startsWith(`${localePath}/stations/`) ||
        currentPath.startsWith('/stations/') ||
        currentPath === `${localePath}/route` ||
        currentPath === '/route' ||
        currentPath.startsWith(`${localePath}/lines/`)
      if (!onMapSurface) return
    }
    // A station closed while the line view is still selected goes back to
    // the line — `LineUrlSync` owns that URL (`/<locale>/lines/<slug>`).
    if (selectedStation === null && store.getState().selectedLine !== null) return
    if (selectedStation === null) {
      // Strip the station-specific params (`line`, `station`) from the
      // URL when the selection clears, so a reload after a route is
      // built does NOT re-hydrate the stale destination station from
      // `?station=…&line=…` and pop the StationCard carousel back up
      // (TASK-275 user-reported bug 2026-05-06). Other params
      // (`from`, `to`, hidden-content gate, analytics campaign tags)
      // MUST be preserved — they describe the route / session state and
      // survive the carousel dismissal.
      const stripped = new URLSearchParams(window.location.search)
      const wasStationParamPresent = stripped.has('station') || stripped.has('line')
      stripped.delete('station')
      stripped.delete('line')
      const search = stripped.toString() === '' ? '' : `?${stripped.toString()}`
      const hash = window.location.hash

      // Route ⇄ station mutual exclusivity + canonical route surface
      // (bug-fix / TASK-275 2026-07-06). A COMPLETE route (`?from` AND `?to`)
      // supersedes an inspected station and lives on the canonical
      // `/<locale>/route` surface. Whenever no station is selected and the
      // route is complete, move there (unless already there) — this covers
      // BOTH the mixed-URL cold load (`/stations/<slug>?from&to`, where
      // UrlToStoreSync suppressed the station hydration and `hasEver` is
      // false) and the build-over-station flow (a station was open, the route
      // just completed and cleared it, `hasEver` true). `search` already has
      // `?station=&line=` stripped and keeps `?from=&to=`. RouteSelectionUrlSync
      // navigates to the SAME `/route` target on route churn, so the two
      // writers converge instead of fighting. `replace: true` — no history
      // pollution.
      const routeComplete = stripped.has('from') && stripped.has('to')
      const canonicalRoutePath = `${localePath}/route`
      if (routeComplete) {
        if (window.location.pathname !== canonicalRoutePath) {
          navigate({ pathname: canonicalRoutePath, search, hash }, { replace: true })
        }
        return
      }

      if (!hasEverSelectedRef.current) {
        // Cold start, never had a selection — nothing to clean up unless
        // the URL already carries stale `?station=&line=` (e.g. user
        // refreshed mid-route-build). In that case do an in-place URL
        // rewrite without nav so UrlToStoreSync doesn't re-hydrate the
        // selection on the next read.
        if (wasStationParamPresent) {
          navigate({ pathname: window.location.pathname, search, hash }, { replace: true })
        }
        return
      }
      // Only navigate home if we're currently on a station route;
      // avoids overriding deliberate navigations to /lines, /oyster-card.
      const path = window.location.pathname
      const isStationRoute =
        path.startsWith('/stations/') ||
        (urlLocale !== undefined && path.startsWith(`/${urlLocale}/stations/`))
      if (isStationRoute) {
        navigate(
          { pathname: localePath !== '' ? `${localePath}` : '/', search, hash },
          { replace: urlReplaceMode },
        )
      } else if (wasStationParamPresent) {
        // Already on '/' (or '/lines', etc.) but URL still has stale
        // station params — strip them in place.
        navigate({ pathname: window.location.pathname, search, hash }, { replace: true })
      }
      return
    }
    // Preserve search + hash on URL sync. Stripping them broke the
    // hidden-content gate (TASK-253 Plan B Task 24): `?showHiddenContent=true`
    // is consumed by useHiddenContentGate, but the first store→URL sync
    // happens before <StationDetails> mounts (schema still loading), so
    // by the time the gate reads window.location.search the query is gone.
    // Other future query params (`?line=`, debug flags, analytics campaign
    // tags) all suffer the same loss-of-state. Keeping search/hash makes
    // the URL the single source of truth across the route swap.
    //
    // Multi-line cluster disambiguation: schema v1.23+ shares one `slug`
    // across cluster siblings (e.g. West Hampstead has 3 records — Jubilee,
    // Mildmay, Thameslink — all with slug "west-hampstead"). Without
    // `?line=<line-slug>` in the URL, `resolveStation` falls back to the
    // cluster's primary line, so a carousel settle on a non-primary
    // sibling is instantly overwritten by `UrlToStoreSync` dispatching
    // the primary back into the store. We write the selected station's
    // line slug into the URL so the round-trip resolves to the same
    // record the carousel just selected.
    const params = new URLSearchParams(window.location.search)
    if (schema !== null) {
      params.set('line', lineSlugFromId(schema, selectedStation.lineId))
      // `?station=<id>` disambiguates clusters that have MULTIPLE
      // stations on the same line — Euston has two Northern-line
      // platforms (northern.940GZZLUEUS-0, northern.940GZZLUEUS-1), Camden Town
      // has three (CTN0/1/2), etc. Without an id-level discriminator
      // the URL→Store sync resolves them all to the first match for
      // `?line=northern` and the user can never settle on the second
      // platform — settling on it triggers a CarouselSettled, the
      // URL→Store resolves back to platform-0, the carousel auto-
      // scrolls to platform-0, and the loop repeats every time the
      // user swipes. Including the id makes the round-trip exact.
      params.set('station', selectedStation.id)
    }
    // schema is non-null whenever slugIndex is (the latter is derived
    // from the former), so by this point we've always set
    // ?line=&station= and params.toString().length > 0 unconditionally.
    // The empty-search branch is defensive only.
    const search =
      /* v8 ignore next */
      params.toString().length > 0 ? `?${params.toString()}` : ''
    const hash = window.location.hash
    const slug = slugIndex.slugById.get(selectedStation.id)
    if (slug === undefined) {
      // Station not in the slug index — either schema drift or an
      // older schema.json. Fall back to id so the URL still reflects
      // state; router-side resolver must also handle id lookup.
      const fallbackPath = `${localePath}/stations/${encodeURIComponent(selectedStation.id)}`
      if (window.location.pathname !== fallbackPath) {
        navigate({ pathname: fallbackPath, search, hash }, { replace: urlReplaceMode })
      }
      return
    }
    const targetPath = `${localePath}/stations/${slug}`
    const pathnameMatches = window.location.pathname === targetPath
    if (!pathnameMatches || window.location.search !== search) {
      // History semantics (back-button regression fix): when the
      // pathname ALREADY matches the target (we're only appending the
      // canonical `?line=&station=` to the entry the router/back-button
      // just landed on), this is a URL *canonicalization* of the current
      // entry — it must REPLACE, never push. A push here inserts a
      // spurious `…/<slug>` → `…/<slug>?line=…` pair into history, so the
      // browser back button pops to the bare-slug entry instead of the
      // previously-visited station (user-reported white-screen / wrong-
      // station-on-back bug; reproduced by back-button-regression.spec).
      // Only a genuine station→station change (pathname differs) honours
      // `urlReplaceMode` (map-tap pushes, carousel-settle replaces).
      const replace = pathnameMatches ? true : urlReplaceMode
      navigate({ pathname: targetPath, search, hash }, { replace })
    }
    // `schema` IS read inside this effect (the `?line=` / `?station=`
    // branch). It transitions null -> loaded once; without it in the
    // deps a station selected before the schema finishes loading would
    // capture `schema === null`, skip the `?line=` param, and never
    // re-run to add it — a real stale-closure hazard.
  }, [selectedStation, urlReplaceMode, navigate, slugIndex, schema, localePath, urlLocale, store])

  return null
}

/**
 * Reads the URL slug + optional `?line=` query param and dispatches
 * `StationSelected` into the store. Mount selects, unmount clears.
 *
 * This is the URL → store half of the bidirectional sync. The previous
 * StationPanel owned this logic; extracted here as a standalone helper
 * so the rendering layer (StationDetailHost) can read selection state
 * from the store rather than from the URL.
 */
function UrlToStoreSync() {
  const schema = useSchema()
  const dispatch = useMetroMapDispatch()
  const { slug } = useParams<{ slug?: string }>()
  const [searchParams] = useSearchParams()

  // Mixed-URL precedence latch (bug-fix 2026-07-06). A URL that mixes a
  // station-detail path (`/stations/<slug>` + `?station=&line=`) with a
  // COMPLETE route (`?from=` AND `?to=`) must resolve to the route only —
  // a built route and an inspected station are mutually exclusive, and the
  // route wins. On such a cold load we must NOT hydrate the station from the
  // slug (that is what re-created `selectedStation` on reload and rendered
  // StationDetails underneath the RouteCard pills). Captured once at
  // mount from the initial URL so it is StrictMode-double-invoke stable.
  const initialSlugRef = useRef(slug)
  // `slug` here is the mount-time value (the `useRef` initializer only keeps
  // the first render's result), so it equals `initialSlugRef.current` without
  // reading a ref during render.
  const routeWonInitialHydrationRef = useRef(
    searchParams.get('from') !== null && searchParams.get('to') !== null && slug !== undefined,
  )

  // Depend on the primitive query-param strings, not the searchParams
  // object. Whenever ANY query param changes (e.g. `?from=…`/`?to=…`
  // written by RouteSelectionUrlSync after a From/To button tap),
  // searchParams gets a new object reference. Memoising on the object
  // would make `resolved` change identity even when only routing-
  // unrelated params moved, which then re-fires the effect below and
  // re-selects the station the user just dismissed (Bug B).
  const lineParam = searchParams.get('line')
  const stationParam = searchParams.get('station')
  const resolved = useMemo(
    () => resolveStation(schema, slug, lineParam, stationParam),
    [schema, slug, lineParam, stationParam],
  )

  // Wait until the schema is loaded before dispatching null (slug not
  // resolved). On a cold load of /stations/<slug>, schema arrives async —
  // dispatching null in the meantime causes SelectedStationUrlSync to
  // navigate away to '/' and back, polluting history. Once the schema is
  // present, a null `resolved` means the slug genuinely doesn't match a
  // station; we surface that by clearing selection.
  //
  // The cleanup-on-unmount clears selection exactly once when the route
  // truly leaves /stations/<slug>. Crucially it does NOT fire on
  // station→station navigation, where the component instance is reused
  // and only `resolved` changes.
  useEffect(() => {
    if (schema === null) return
    // Once the router leaves the initial mixed slug (SelectedStationUrlSync
    // drops `/stations/<slug>` to the index), the "route won" latch is
    // spent — normal station selection resumes for every later navigation
    // (e.g. a §13 map-tap that re-opens StationDetails over the route).
    if (routeWonInitialHydrationRef.current && slug !== initialSlugRef.current) {
      routeWonInitialHydrationRef.current = false
    }
    if (routeWonInitialHydrationRef.current) {
      // Mixed URL cold-load: the complete `?from&to` route supersedes the
      // `/stations/<slug>` station. Do NOT select the station; clear so
      // SelectedStationUrlSync navigates the path to the index (route only).
      dispatch({ kind: 'StationSelected', snapshot: null })
      return
    }
    if (resolved === null) {
      // §3 `deeplink_resolve_failed` — a `/stations/<slug>` deep link arrived
      // (a real slug in the URL) but resolved to no station (bad/stale slug,
      // schema drift). Only fire for a slug-bearing deep link, not the bare
      // map home (`slug === undefined`, where a null resolve is normal). The
      // selection clears either way; this just surfaces the dead link.
      if (slug !== undefined) {
        reportHealthError(
          HEALTH_ID.deeplinkResolveFailed,
          `deeplink /stations/${slug} resolved to no station`,
          {
            domain: 'deeplink',
            severity: 'warning',
            errorKind: 'not_found',
            extra: { scheme: 'station' },
          },
        )
      }
      dispatch({ kind: 'StationSelected', snapshot: null })
      return
    }
    dispatch({
      kind: 'StationSelected',
      snapshot: stationSnapshotFromSchema(resolved),
    })
  }, [schema, resolved, slug, dispatch])

  useEffect(() => {
    return () => {
      dispatch({ kind: 'StationSelected', snapshot: null })
    }
  }, [dispatch])

  return null
}

/**
 * Reads the current selection from the store + the schema, builds the
 * carousel cards array, and renders <StationCardOrDetailsRouter>. The
 * router branches on viewport: phone → <StationCard>, tablet/desktop
 * → <StationDetails>.
 *
 * For MVP, the carousel cards array contains only the currently-selected
 * card. The full sliding-window of N stations around the selection is
 * a follow-up (BACKLOG).
 */
function StationDetailHost() {
  const schema = useSchema()
  const store = useMetroMapStore()
  const dispatch = useMetroMapDispatch()
  const selectedStation = useSyncExternalStore(
    store.subscribe,
    () => store.getState().selectedStation,
  )
  // The EFFECTIVE theme — the signal that picks the map's MMTG
  // geometry, night-tube included; StationCard line colors must
  // match the map (2026-07 color contract).
  const theme = useSyncExternalStore(store.subscribe, () => effectiveTheme(store.getState()))
  const stationStatuses = useStationStatuses()
  // I (Feedback): the StationDetails report-issue tap opens the Feedback
  // form with the `stationDetails` context (mirrors iOS presenting the
  // FeedbackModule from station details).
  const { openFeedback } = useFeedback()
  const onStationReportIssue = useCallback(() => openFeedback('stationDetails'), [openFeedback])

  // From/To handlers fired by the compact carousel's per-card buttons.
  // Both resolve the tapped id against the schema (the carousel only
  // carries the id), dispatch the appropriate route-selection event,
  // and immediately dismiss the carousel by clearing `selectedStation`
  // — the user's intent is "this is my origin/destination", not "keep
  // looking at this card". The carousel dismiss uses
  // `urlReplaceMode: true` so the back-button does not have to walk
  // back through the now-routed-away station.
  //
  // Both bail when the schema / resolver miss — schema drift between
  // event time and dispatch time is silently dropped, matching the
  // behaviour in `RouteSelectionLocalStorageSync` for unresolved slugs.
  const handleCardFromTap = useCallback(
    (stationId: string): void => {
      if (schema === null) return
      const resolved = resolveStationById(schema, stationId)
      if (resolved === null) return
      dispatch({ kind: 'RouteFromSelected', station: stationSnapshotFromSchema(resolved) })
      dispatch({ kind: 'StationSelected', snapshot: null, urlReplaceMode: true })
    },
    [schema, dispatch],
  )

  const handleCardToTap = useCallback(
    (stationId: string): void => {
      if (schema === null) return
      const resolved = resolveStationById(schema, stationId)
      if (resolved === null) return
      dispatch({ kind: 'RouteToSelected', station: stationSnapshotFromSchema(resolved) })
      dispatch({ kind: 'StationSelected', snapshot: null, urlReplaceMode: true })
    },
    [schema, dispatch],
  )

  // Carousel scroll-settle → CarouselSettled. Extracted from the inline
  // JSX closure so `useStationCardAnalytics` can wrap it (emit
  // `station_card_settled` alongside the dispatch).
  const handleCardSettled = useCallback(
    (id: string): void => {
      // onSettled only fires from the carousel, which only renders when
      // selectedStation is non-null (the router returns null otherwise).
      // The guard below is purely defensive.
      /* v8 ignore start */
      if (store.getState().selectedStation === null || schema === null) return
      /* v8 ignore stop */
      // Cluster siblings live on different lines (e.g. Baker Street has
      // 5 lines through one interchange). The snapshot must carry the
      // settled station's OWN lineId / canvasX,Y / localName.
      const target = schema.stations.find((st) => st.id === id)
      // settle id always comes from cardsForCarousel, which is itself
      // derived from the schema. A non-matching id only occurs under
      // schema drift mid-render.
      /* v8 ignore start */
      if (target === undefined) return
      /* v8 ignore stop */
      dispatch({
        kind: 'CarouselSettled',
        snapshot: {
          id: target.id,
          canvasX: target.x,
          canvasY: target.y,
          lineId: target.line.id,
          localName: target.localName,
        },
      })
    },
    [store, schema, dispatch],
  )

  // Resolve actual transfers from the schema for the currently-selected
  // station. Each station has a `transferStationsIds` array; for every
  // referenced id we emit the `{ id, stationName, lineLocalName,
  // lineHexColor }` shape that <StationDetails> and <StationCard>
  // consume. Same array is reused for both surfaces — keeps the
  // expanded sidebar and the compact carousel transfer list in lockstep.
  const transfers = useMemo(() => {
    if (selectedStation === null || schema === null) return []
    const current = schema.stations.find((st) => st.id === selectedStation.id)
    if (current === undefined) return []
    const ids: ReadonlyArray<string> = current.transferStationsIds ?? []
    return (
      ids
        .map((id) => schema.stations.find((st) => st.id === id))
        /* v8 ignore next — filter drops dangling transferStationsIds (schema
         drift). Real schemas don't carry orphan transfer references; the
         guard exists to keep us crash-free if a future schema bump does. */
        .filter((st): st is NonNullable<typeof st> => st !== undefined)
        .map((st) => ({
          id: st.id,
          stationName: st.localName,
          lineLocalName: st.line.localName ?? '',
          lineHexColor: st.line.hexColor,
        }))
    )
  }, [selectedStation, schema])

  // Cluster carousel: emits the currently-selected station card +
  // every transfer-station sibling at the same physical location. iOS
  // builds the same array in `StationCardCollectionViewReducer` —
  // sliding through the carousel cycles between the lines that pass
  // through the same interchange (e.g. Baker Street has 5 cards:
  // Bakerloo / Circle / Hammersmith&City / Jubilee / Metropolitan).
  //
  // Bug 4 fix: order MUST be stable across selection changes within
  // the same cluster. Previously we put `current` first; when the user
  // swiped to the next card, `selectedStation` updated → `useMemo`
  // re-ran → cards reordered → React reconciled + scroll position
  // reset, breaking the swipe past the second card. Now we sort the
  // cluster by station id (deterministic, schema-stable) so the
  // carousel array is identical regardless of which card in the
  // cluster is selected — the highlight follows currentId, the order
  // never changes. iOS-parity note: the iOS reducer also keys
  // selection by id and doesn't reorder the snapshot array on swipe.
  const cardsForCarousel = useMemo(() => {
    if (selectedStation === null || schema === null) return []
    // Cluster cardinality MUST be stable across cluster-internal swipes.
    // `transferStationsIds` is asymmetric — at Euston the Victoria-line
    // record lists BOTH Northern-line platforms (EUS0, EUS1), but each
    // Northern record lists only the OTHER line, not its same-line
    // sibling (same-line different-platform isn't an interchange in the
    // schema sense). Using only `current.transferStationsIds` makes the
    // carousel size oscillate (3 ↔ 4 cards) as state moves between
    // Northern and Victoria, slot indices shift under the unchanged
    // scrollLeft, and IntersectionObserver settles on whichever station
    // happens to occupy slot N this render — which dispatches a new
    // CarouselSettled, re-derives the cluster, swaps slot N's station
    // back, and the loop repeats forever (user-reported bug at Euston
    // bouncing EUS1 ↔ vic without input).
    //
    // Fix: BFS the transfer graph from current, collect the connected
    // component, sort by id. The closure is identical for every member
    // of the component, so the carousel is stable regardless of which
    // member is selected. See `state/carouselCluster.ts` for the helper.
    return buildCarouselCluster(schema, selectedStation.id, theme)
  }, [selectedStation, schema, theme])

  // Mobile-only modal: tracks whether the StationCard "Station
  // information" tap has been triggered. The render-gate below
  // (`!isExpanded && selectedStation !== null && detailsOpen`) means
  // the boolean can persist as `true` across viewport changes or
  // selection clears without leaking stale UI — when conditions stop
  // being met the modal simply unmounts. Re-tapping at compact will
  // re-open with fresh data.
  const [detailsOpen, setDetailsOpen] = useState(false)
  // Live favourites — the shared `FavoritesService` (TASK-318 §16) is
  // written by the StationDetails favourite toggle below and read by
  // Suggester's idle Favorites section. Subscribing here re-renders
  // the modal so the toggle reflects instantly.
  const favoriteIds = useSyncExternalStore(favoritesService.subscribe, favoritesService.getAll)
  const isExpanded = useSyncExternalStore(
    (callback) => {
      const mql = window.matchMedia(`(min-width: ${BREAKPOINT_PX}px)`)
      mql.addEventListener('change', callback)
      return () => mql.removeEventListener('change', callback)
    },
    () => window.matchMedia(`(min-width: ${BREAKPOINT_PX}px)`).matches,
    () => false,
  )

  // GAP-1 parity: the compact StationCard carousel emits the
  // cross-platform `station_card_*` set (was dark on web — facade
  // existed, host never called it). Wrap the card callbacks so each
  // tap / settle emits, and fire `_opened` once per station + `_closed`
  // on dismissal. `active` = the carousel is actually rendered (phone
  // viewport + a station selected); the tablet sidebar uses
  // StationDetails analytics instead.
  const cardAnalytics = useStationCardAnalytics({
    stationId: selectedStation?.id ?? null,
    active: !isExpanded && selectedStation !== null,
    transfersCount: transfers.length,
    reporter: realMetroAnalyticsReporter,
    onFromTap: handleCardFromTap,
    onToTap: handleCardToTap,
    onDetailsTap: () => setDetailsOpen(true),
    onSettled: handleCardSettled,
  })

  // G-10 `line_status_viewed` (surface: 'station'): resolve the selected
  // station's status-banner variant from the live snapshot the same way the
  // carousel does (`resolveStatusVariant` by naptanId), and its line-schema
  // id from the snapshot's `lineId`. The hook fires once per fresh banner
  // appearance. `null` variant (no disruption / no resolved line) → no emit.
  // Computed here, outside the `schema === null` early return below, so the
  // hook runs unconditionally (React rules-of-hooks).
  const selectedNaptanId =
    selectedStation === null || schema === null
      ? undefined
      : schema.stations.find((st) => st.id === selectedStation.id)?.naptanId
  const lineStatusVariant =
    selectedStation === null
      ? null
      : (resolveStatusVariant(selectedNaptanId, stationStatuses) ?? null)
  useLineStatusViewedAnalytics({
    lineId: selectedStation?.lineId ?? null,
    variant: lineStatusVariant,
    reporter: realMetroAnalyticsReporter,
  })

  if (schema === null) return null

  // Shared transfer-tap handler — used by the inline sidebar AND the
  // mobile modal. Lives inline in StationDetailHost (closes over
  // `dispatch` + `schema`) rather than in buildStationDetailsProps
  // because the helper has no access to the store.
  const onTransferTap = (transferId: string): void => {
    // Map-tap behaviour: push history (urlReplaceMode=false default)
    // so the user can back-button between transfer hops.
    const target = schema.stations.find((st) => st.id === transferId)
    // transfer ids come from `transfers`, which is filtered against
    // the schema; a missing target requires schema drift mid-render.
    /* v8 ignore start */
    if (target === undefined) return
    /* v8 ignore stop */
    dispatch({
      kind: 'StationSelected',
      snapshot: {
        id: target.id,
        canvasX: target.x,
        canvasY: target.y,
        lineId: target.line.id,
        localName: target.localName,
      },
    })
    // The modal stays open — `selectedStation` updates and `<StationDetailsModal>`
    // re-mounts its contents for the new station. Closing on transfer-tap was a
    // misread of the UX intent (the user wants to read the next station's details
    // without dismissing); keep state cohesive between map-tap (closes nothing)
    // and transfer-tap (also closes nothing — only the station identity changes).
  }

  // Resolve the station record once for the modal mount. Reuses the
  // same lookup the inline router does, then funnels through
  // buildStationDetailsProps so the two surfaces stay in lockstep.
  const stationData =
    selectedStation === null
      ? null
      : (schema.stations.find((st) => st.id === selectedStation.id) ?? null)
  const modalDetailsProps =
    !isExpanded && detailsOpen && stationData !== null
      ? buildStationDetailsProps(stationData, schema, transfers, {
          onTransferTap,
          // TASK-318 §16 — persist the favourite through the shared
          // FavoritesService so Suggester's idle Favorites section
          // reflects it. Replaces the former no-op stub.
          // Стор кластерный (naptanId): станция без наптана не может быть
          // добавлена — тоггл для неё no-op, как и на iOS/Android.
          onFavoriteTap: () => {
            if (stationData.naptanId !== undefined) favoritesService.toggle(stationData.naptanId)
          },
          onPaywallTap: () => {},
          onReportIssueTap: onStationReportIssue,
          isFavorite:
            stationData.naptanId !== undefined && favoriteIds.includes(stationData.naptanId),
          // Web is 100% free (no paywall, product contract §0.4) — every
          // user gets premium, so `isPremium` is hard-`true`: the
          // TimetableUpsellBanner never renders on the mobile modal path
          // and the timetable shows free. Mirrors the tablet/desktop
          // inline mount in StationCardOrDetailsRouter (isPremium={true}).
          // Without this the builder default (`false`) leaked the upsell
          // banner onto the mobile StationDetails sheet (mobile-web bug).
          isPremium: true,
        })
      : null

  return (
    <>
      <StationCardOrDetailsRouter
        selectedStation={selectedStation}
        schema={schema}
        cardsForCarousel={cardsForCarousel}
        transfers={transfers}
        onSettled={cardAnalytics.onSettled}
        onTransferTap={onTransferTap}
        // Persist through the shared FavoritesService (TASK-318 §16) so
        // Suggester's idle Favorites section reflects the toggle — the
        // SAME singleton the reader subscribes to (`favoriteIds` above).
        // Mirrors the phone modal path (`modalDetailsProps.onFavoriteTap`);
        // replaces the former `noopHiddenRowHandler` stub that left the
        // expanded (tablet/desktop) "Add to favourites" button inert.
        // `stationData` is always set when the router surfaces the row.
        onFavoriteTap={() => {
          /* v8 ignore start */
          if (stationData === null) return
          /* v8 ignore stop */
          // Кластерный ключ; без наптана тоггл — no-op (iOS/Android parity).
          if (stationData.naptanId !== undefined) favoritesService.toggle(stationData.naptanId)
        }}
        favoriteIds={favoriteIds}
        onPaywallTap={noopHiddenRowHandler}
        onReportIssueTap={onStationReportIssue}
        onCardDetailsTap={cardAnalytics.onDetailsTap}
        onCardFromTap={cardAnalytics.onFromTap}
        onCardToTap={cardAnalytics.onToTap}
        statuses={stationStatuses}
        // Banner tap → DisruptionDetails: currently a no-op stub (spec §6 output).
        // DisruptionDetails is in @metro/suggester; full wiring is a follow-up
        // (requires pushing the DisruptionPresentation into the MetroMap store and
        // mounting a DisruptionDetailsView over the carousel). The callback is
        // correctly plumbed here so the tap is live and the handler can be wired
        // without further prop changes.
        onStatusBannerTap={undefined}
        // Real reporter — the §8 StationDetails events forward to
        // GA4 + Amplitude (no-op outside production).
        reporter={realMetroAnalyticsReporter}
      />
      {modalDetailsProps !== null && (
        <StationDetailsModal
          detailsProps={modalDetailsProps}
          reporter={realMetroAnalyticsReporter}
          onClose={() => setDetailsOpen(false)}
        />
      )}
      {/*
        Mobile-only floating route-card. Self-gates internally on
        viewport (matchMedia < 768px) + carousel takeover
        (`selectedStation !== null`). Mounted at StationDetailHost level
        alongside the carousel router so the two phone-bottom surfaces
        share a single conceptual slot — the overlay renders ONLY when
        the carousel does NOT.
      */}
      <MobileRouteCardOverlay selectedStation={selectedStation} />
      {/* Phone line view card / sheet — shares the bottom slot with the
          route card, which steps aside while the line view is open. */}
      <LineViewHost surface="card" />
    </>
  )
}
